import { useCallback, useMemo } from 'react';
import { UseQueryOptions } from '@tanstack/react-query';

import {
  api,
  UserPreference,
  UserPreferenceKey,
  UserPreferencesError,
  UserPreferencesResponse,
} from '@/api';
import { useIsDDFeatureFlagEnabled } from '@/core/data-dog';

export function useUserPreferences(
  config?: Partial<UseQueryOptions<UserPreferencesResponse, UserPreferencesError>>,
) {
  const isUserPreferencesEnabled = useIsDDFeatureFlagEnabled('FCT_USER_PREFERENCES');

  const query = api.fct.userPreference.query.useGetUserPreferences({
    ...config,
    enabled: isUserPreferencesEnabled && (config?.enabled ?? true),
  });

  const data = useMemo(() => {
    const raw = isUserPreferencesEnabled ? query.data : undefined;

    return Array.isArray(raw) ? raw : ([] as UserPreference[]);
  }, [isUserPreferencesEnabled, query.data]);

  const preferencesMap = useMemo(() => new Map(data.map((pref) => [pref.key, pref])), [data]);

  const getPreference = useCallback(
    (key: UserPreferenceKey) => preferencesMap.get(key),
    [preferencesMap],
  );

  return {
    ...query,
    data,
    getPreference,
    isEnabled: isUserPreferencesEnabled,
    preferencesMap,
  };
}
